import { useMemo } from 'react';

import {
  StandardsVariant,
  TrackAssessmentCategory,
  TrackAssessmentCategorySlug,
} from '@backrs_ui/common-ui';

import { useGetDimensionCriteriaQuery } from '@backrs_ui/grow-ui/src/redux/trackAssessment/trackAssessment.api';

import { useAcademicStandards } from './useAcademicStandards';

export type UnlockStandards = Readonly<{
  category: TrackAssessmentCategory;
  variant: StandardsVariant;
}>;

/**
 * Per-category standards: academic-foundations filtered to the given tenure, the
 * rest filtered to the given grade. A category is absent when its data is
 * unavailable.
 */
export const useUnlockStandards = (
  tenure: 'High School' | 'Post High School' | undefined,
  academicGradeId: number | undefined,
  gradeLabel: string,
): Partial<Record<TrackAssessmentCategorySlug, UnlockStandards>> => {
  const { category: academicCategory, filteredStandards } = useAcademicStandards(tenure);
  const { data: dimensionCriteria } = useGetDimensionCriteriaQuery({});

  return useMemo(() => {
    const byCategory: Partial<Record<TrackAssessmentCategorySlug, UnlockStandards>> = {};

    if (academicCategory && filteredStandards?.length) {
      byCategory['academic-foundations'] = {
        category: academicCategory,
        variant: { kind: 'by-track', standards: filteredStandards },
      };
    }

    if (!dimensionCriteria || !academicGradeId) return byCategory;

    for (const row of dimensionCriteria) {
      const category = row.dimension?.category;

      if (!category || category.slug === 'academic-foundations') continue;
      if (row.academic_grade_id !== academicGradeId) continue;

      const entry = byCategory[category.slug];

      if (entry?.variant.kind === 'by-level') entry.variant.criteria.push(row);
      else
        byCategory[category.slug] = {
          category,
          variant: { kind: 'by-level', criteria: [row], gradeLabel },
        };
    }

    return byCategory;
  }, [academicCategory, filteredStandards, dimensionCriteria, academicGradeId, gradeLabel]);
};
